import React, { FunctionComponent, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'

import { skipToken } from '@reduxjs/toolkit/query'
import { AxiosError } from 'axios'
import { differenceInYears, parseISO } from 'date-fns'
import styled from 'styled-components/native'

import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Divider } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/divider/Divider'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { Title, Size as TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { InlineErrorBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineErrorBanner/InlineErrorBanner'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { NpTestPlanBuildPanel } from './components/NpTestPlanBuildPanel'
import { NpTestPlanBuiltPanel } from './components/NpTestPlanBuiltPanel'
import { NpTestPlanProgress, NpTestPlanProgressStep } from './components/NpTestPlanProgress'
import { NpTestPlanTimeSummary } from './components/NpTestPlanTimeSummary'
import { NpTestPlanWaitingBanner } from './components/NpTestPlanWaitingBanner'
import { NP_TEST_PLAN_CONDITIONS } from './data/npTestPlanConditions'
import { groupMeasures } from './data/npTestPlanMeasureUtils'
import { useCreateNpTestPlanMutation, useGetNpTestPlanSummaryQuery } from './data/npTestPlansApi'
import {
  NpTestPlanGradeLevel,
  NpTestPlanMeasure,
  NpTestPlanMeasureScope,
  NpTestPlanModality,
  NpTestPlanStatus,
} from './data/npTestPlanTypes'
import { useNpTestPlanEntrypoint } from './hooks/useNpTestPlanEntrypoint'
import { useNpTestPlanScheduledModality } from './hooks/useNpTestPlanScheduledModality'
import { useUpdateNpTestPlanMeasures } from './hooks/useUpdateNpTestPlanMeasures'
import { NpTestPlanSection } from './npTestPlanConstants'
import { CLIENT_EVALUATION_ADD_MEASURES, CLIENT_HOME, CLIENT_NOTES } from '../../common/constants/routingConstants'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../data/store'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { setToastContent } from '../data/ltToastAutoActions'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

const Layout = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: spacing['48px'],
}))

const Content = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flex: 1,
  gap: spacing['24px'],
}))

const ContentHeader = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacing['16px'],
}))

const HeadingGroup = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['16px'],
}))

/**
 * [ENGBEND-12942] Evaluation tab of the member chart, scoped to the neuropsych episode currently
 * selected. Only Build plan exists so far, so the panel on the left is progress, not navigation.
 */
export const NpTestPlan: FunctionComponent = () => {
  const { formatMessage, formatDate } = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const patientId = useSelector(getSelectedClientId)
  const { data: clientDetails } = useSelectedClientData()
  const { data: episode, isLoading: isLoadingEpisode } = useSelectedEpisodeForClient() as {
    data: Episode | undefined
    isLoading: boolean
  }
  const { isVisible, isBlocked, isLoading: isLoadingEntrypoint } = useNpTestPlanEntrypoint(episode)

  const {
    data: summary,
    isLoading: isLoadingSummary,
    isFetching: isFetchingSummary,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useGetNpTestPlanSummaryQuery(
    isVisible && episode?.id && patientId ? { episodeId: episode.id, patientId } : skipToken,
  )
  const { scheduledModality, isLoading: isLoadingScheduledModality } = useNpTestPlanScheduledModality(
    isVisible ? episode?.id : undefined,
  )
  const [createNpTestPlan, { isLoading: isCreatingPlan }] = useCreateNpTestPlanMutation()
  const { updateMeasures, isSaving } = useUpdateNpTestPlanMeasures(episode?.id)

  // Age filters which test measures are appropriate, and the ticket asks for it to be automated
  // from the date of birth rather than asked of the provider, so it is never rendered.
  const patientAge = clientDetails?.date_of_birth
    ? differenceInYears(new Date(), parseISO(clientDetails.date_of_birth))
    : null

  const status = summary?.status ?? NpTestPlanStatus.NOT_STARTED
  const isDraft = status === NpTestPlanStatus.DRAFT
  const isPendingLeadReview = status === NpTestPlanStatus.PENDING_LEAD_REVIEW

  const planMeasures = useMemo(() => summary?.measures ?? [], [summary?.measures])
  const planSections = useMemo(() => groupMeasures(planMeasures), [planMeasures])

  const waitingOnIntakeNote = formatMessage({
    defaultMessage: 'Waiting for the intake note to be completed',
    description: 'Explains that a neuropsych test plan cannot be built until the intake note is completed',
  })
  const waitingOnLeadBuild = formatMessage({
    defaultMessage: 'Waiting for Neuropsych lead to finish build',
    description: 'Explains that the Neuropsych lead has not finished building the test plan yet',
  })

  const buildPlanLabel = formatMessage({
    defaultMessage: 'Build plan',
    description: 'Label for the step where a neuropsych test plan is built',
  })

  const progressSteps: NpTestPlanProgressStep[] = useMemo(
    () => [
      {
        section: NpTestPlanSection.BUILD_PLAN,
        displayName: buildPlanLabel,
        meta: summary?.dueDate
          ? formatMessage(
              {
                defaultMessage: 'Due {dueDate}',
                description: 'Date a neuropsych test plan is due, shown next to the build plan step',
              },
              { dueDate: formatDate(summary.dueDate, { month: 'short', day: 'numeric', year: 'numeric' }) },
            )
          : undefined,
        details: planSections.length ? <NpTestPlanTimeSummary sections={planSections} /> : undefined,
      },
      {
        section: NpTestPlanSection.MANAGE_PLAN,
        displayName: formatMessage({
          defaultMessage: 'Manage plan',
          description: 'Label for the step where a neuropsych test plan is managed',
        }),
      },
      {
        section: NpTestPlanSection.FEEDBACK_REPORT,
        displayName: formatMessage({
          defaultMessage: 'Feedback report',
          description: 'Label for the step where the neuropsych feedback report is shared',
        }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summary?.dueDate, buildPlanLabel, formatMessage, formatDate, planSections],
  )

  const onAddMeasures = ({ measureType, informant }: NpTestPlanMeasureScope) =>
    navigate(`${CLIENT_EVALUATION_ADD_MEASURES.route}?${new URLSearchParams({ measureType, informant })}`)

  const onRemoveMeasure = ({ id }: NpTestPlanMeasure) =>
    updateMeasures(planMeasures.filter((measure) => measure.id !== id).map((measure) => measure.id))

  const onStartBuild = ({
    modality,
    conditionId,
    gradeLevel,
  }: {
    modality: NpTestPlanModality
    conditionId: string
    gradeLevel?: NpTestPlanGradeLevel
  }) => {
    if (!episode?.id || !patientId) {
      return
    }
    createNpTestPlan({ episodeId: episode.id, patientId, modality, conditionId, patientAge, gradeLevel })
      .unwrap()
      .catch((error: AxiosError<{ message?: string }>) => {
        dispatch(
          setToastContent({
            text:
              error?.response?.data?.message ||
              formatMessage({
                defaultMessage: "Your test plan couldn't be created. Please try again.",
                description: 'Toast error shown when creating a neuropsych test plan fails',
              }),
            toastType: 'error',
            id: 'NpTestPlan-create-error',
          }),
        )
      })
  }

  if (!isVisible) {
    return <Navigate to={CLIENT_HOME.route} replace />
  }

  if (isLoadingEpisode || isLoadingEntrypoint || isLoadingSummary || isLoadingScheduledModality) {
    return <LoadingIndicator topPadding={0} />
  }

  const renderBuildPlan = () => {
    if (isBlocked) {
      return (
        <NpTestPlanWaitingBanner
          testIdName='NpTestPlan-intakeNoteWaitingBanner'
          title={waitingOnIntakeNote}
          description={formatMessage({
            defaultMessage: 'The test plan can be built once the intake note has been completed.',
            description: 'Explains what has to happen before a neuropsych test plan can be built',
          })}
        />
      )
    }
    // Only a 404 means there is no plan yet: offering Start build here could overwrite a saved one.
    if (isSummaryError) {
      return (
        <InlineErrorBanner
          testId={tID('NpTestPlan-summaryError')}
          text={formatMessage({
            defaultMessage: "The test plan couldn't be loaded.",
            description: 'Error shown when the neuropsych test plan for an episode fails to load',
          })}
          trailingLink={
            <Link
              testID={tID('NpTestPlan-summaryRetry')}
              size={BodyTextSize.DEFAULT}
              onPress={refetchSummary}
              text={formatMessage({
                defaultMessage: 'Try again',
                description: 'Link that retries loading the neuropsych test plan',
              })}
            />
          }
        />
      )
    }
    if (isPendingLeadReview) {
      return (
        <NpTestPlanWaitingBanner
          testIdName='NpTestPlan-leadBuildWaitingBanner'
          title={waitingOnLeadBuild}
          description={formatMessage({
            defaultMessage: 'This should be completed within the next 2 business days.',
            description: 'Sets expectations for how long the Neuropsych lead takes to finish the build',
          })}
        />
      )
    }
    if (isDraft) {
      return (
        <NpTestPlanBuiltPanel
          measures={planMeasures}
          memberName={clientDetails?.preferred_first_name || clientDetails?.first_name || ''}
          onAddMeasures={onAddMeasures}
          onRemoveMeasure={onRemoveMeasure}
          // Each removal sends the full list, so it has to wait for the previous one to land.
          isRemoveDisabled={isSaving || isFetchingSummary}
        />
      )
    }
    return (
      // The chart re-renders this outlet in place when the episode changes; keying on the episode
      // drops the modality and condition picked for the episode that is no longer on screen.
      <NpTestPlanBuildPanel
        key={episode?.id}
        conditions={NP_TEST_PLAN_CONDITIONS}
        isCreatingPlan={isCreatingPlan}
        scheduledModality={scheduledModality}
        memberFirstName={clientDetails?.preferred_first_name || clientDetails?.first_name || ''}
        onStartBuild={onStartBuild}
      />
    )
  }

  return (
    <Layout testID={tID('NpTestPlan')}>
      <NpTestPlanProgress steps={progressSteps} activeSection={NpTestPlanSection.BUILD_PLAN} />
      <Content>
        <ContentHeader>
          <HeadingGroup>
            <Title size={TitleSize.SMALL} text={buildPlanLabel} />
            {summary?.battery && (
              <BodyText
                testID={tID('NpTestPlan-battery')}
                size={BodyTextSize.SMALL}
                text={formatMessage(
                  {
                    defaultMessage: '{modality, select, inPerson {In person} other {Virtual}} • {conditionName}',
                    description: 'The modality and condition a neuropsych test plan was built from',
                  },
                  { modality: summary.battery.modality, conditionName: summary.battery.conditionName },
                )}
              />
            )}
          </HeadingGroup>
          {!isBlocked && (
            <Link
              testID={tID('NpTestPlan-reviewIntakeNote')}
              size={BodyTextSize.DEFAULT}
              onPress={() => navigate(CLIENT_NOTES.route)}
              text={formatMessage({
                defaultMessage: 'Review intake note',
                description: 'Link to the note the neuropsych test plan is built from',
              })}
            />
          )}
        </ContentHeader>
        {isDraft && <Divider />}
        {renderBuildPlan()}
      </Content>
    </Layout>
  )
}

export default NpTestPlan
